import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { S } from '../utils/styles';

export default function NewOrder() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [allSizes, setAllSizes] = useState([]);
  const [priceSuggestions, setPriceSuggestions] = useState([]); // fix #3
  const [form, setForm] = useState({
    customer_id:'', pi_number:'', customer_po:'', order_date:new Date().toISOString().split('T')[0],
    expected_ship_date:'', shipping_terms:'FOB', payment_terms:'', currency:'USD',
    destination_port:'', hs_code:'73079910', special_instructions:''
  });
  const [items, setItems] = useState([
    { product_code:'', size:'', qty_ordered:'', price_per_unit:'', section_header:'', line_notes:'' }
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showProductSug, setShowProductSug] = useState(null);
  const [showSizeSug, setShowSizeSug] = useState(null);
  const [showPriceSug, setShowPriceSug] = useState(null); // fix #3
  const tableRef = useRef();

  // Fix #5: default expected_ship_date to current year (leave blank, but min = today)
  const todayStr = new Date().toISOString().split('T')[0];

  useEffect(() => {
    api.get('/customers').then(r => setCustomers(r.data));
    api.get('/products').then(r => setProducts(r.data));
    api.get('/products/meta/all-sizes').then(r => setAllSizes(r.data));
  }, []);

  const onCustomer = id => {
    const c = customers.find(c => c.id == id);
    setForm(f => ({ ...f, customer_id:id,
      shipping_terms: c?.shipping_terms || f.shipping_terms,
      payment_terms: c?.payment_terms || f.payment_terms,
      currency: c?.currency || f.currency }));
    setPriceSuggestions([]); // reset suggestions on customer change
  };

  const f = k => v => setForm(p => ({...p, [k]:v}));

  const addRow = () => setItems(i => [...i, { product_code:'', size:'', qty_ordered:'', price_per_unit:'', section_header:'', line_notes:'' }]);
  const addSectionHeader = () => setItems(i => [...i, { product_code:'', size:'', qty_ordered:'', price_per_unit:'', section_header:'NEW SECTION', line_notes:'', isHeader:true }]);
  const duplicateRow = (i) => {
    const row = items[i];
    const newRow = { ...row, qty_ordered:'', price_per_unit: row.price_per_unit };
    setItems(arr => [...arr.slice(0,i+1), newRow, ...arr.slice(i+1)]);
  };
  const removeRow = i => items.length > 1 && setItems(arr => arr.filter((_,idx) => idx!==i));
  const updateRow = (i,k,v) => setItems(arr => arr.map((it,idx) => idx===i ? {...it,[k]:v} : it));

  const prodSuggestions = (search) => !search ? [] : products.filter(p =>
    p.code.toLowerCase().includes(search.toLowerCase()) || p.name.toLowerCase().includes(search.toLowerCase())
  ).slice(0,8);

  const sizeSuggestions = (search) => !search ? [] : allSizes.filter(s =>
    s.toLowerCase().includes(search.toLowerCase())
  ).slice(0,8);

  // Fix #3: load price+size suggestions when product_code selected for a customer
  const loadPriceSuggestions = async (customerId, productCode, rowIndex) => {
    if (!customerId || !productCode) return;
    try {
      const { data } = await api.get(`/orders/suggest/prices?customer_id=${customerId}&product_code=${encodeURIComponent(productCode)}`);
      if (data.length > 0) {
        setPriceSuggestions(data);
        setShowPriceSug(rowIndex);
      }
    } catch(e) {}
  };

  const applyPriceSuggestion = (rowIndex, sug) => {
    updateRow(rowIndex, 'size', sug.size);
    updateRow(rowIndex, 'price_per_unit', String(sug.price_per_unit));
    setShowPriceSug(null);
  };

  // Fix #4: column-by-column paste — completely reworked
  const handlePaste = (e, rowIndex, field) => {
    const text = e.clipboardData.getData('text');
    const lines = text.split(/\r?\n/).map(l => l.trimEnd()).filter(l => l.trim());
    if (lines.length === 0) return;

    const hasMultipleCols = lines.some(l => l.includes('\t'));

    if (hasMultipleCols) {
      // Multi-column paste from Excel: fill multiple fields per row
      e.preventDefault();
      const colFields = ['section_header', 'product_code', 'size', 'qty_ordered', 'price_per_unit', 'line_notes'];
      setItems(arr => {
        const next = [...arr];
        lines.forEach((line, i) => {
          const cols = line.split('\t');
          const targetIdx = rowIndex + i;
          const newRow = targetIdx < next.length
            ? { ...next[targetIdx] }
            : { product_code:'', size:'', qty_ordered:'', price_per_unit:'', section_header:'', line_notes:'' };
          cols.forEach((val, colIdx) => {
            if (colFields[colIdx]) newRow[colFields[colIdx]] = val.trim();
          });
          if (targetIdx < next.length) next[targetIdx] = newRow;
          else next.push(newRow);
        });
        return next;
      });
    } else if (lines.length > 1) {
      // Fix #4: single-column paste — fill only the CURRENT column, expand rows as needed
      e.preventDefault();
      setItems(arr => {
        const next = [...arr];
        lines.forEach((val, i) => {
          const targetIdx = rowIndex + i;
          const cleanVal = val.trim();
          if (targetIdx < next.length) {
            next[targetIdx] = { ...next[targetIdx], [field]: cleanVal };
          } else {
            // Auto-expand rows
            next.push({ product_code:'', size:'', qty_ordered:'', price_per_unit:'', section_header:'', line_notes:'', [field]: cleanVal });
          }
        });
        return next;
      });
    }
    // Single value: let browser handle naturally
  };

  const totalValue = items.reduce((s,i) => s + ((parseFloat(i.qty_ordered)||0) * (parseFloat(i.price_per_unit)||0)), 0);

  const downloadTemplate = () => {
    const rows = [
      ['section_header','product_code','size','qty_ordered','price_per_unit','line_notes'],
      ['','RL','20-25mm','5000','0.45',''],
      ['NC Clips','IGCH50NC','','16720','0.147',''],
      ['NC Clips','IGCH90NC','','22320','0.187',''],
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'byldr_order_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const save = async () => {
    setError('');
    const validItems = items.filter(i => i.size && i.qty_ordered);
    if (!form.customer_id || !form.pi_number || !form.order_date) return setError('Customer, PI number and order date are required');
    if (validItems.length === 0) return setError('Add at least one line item with size and quantity');
    setSaving(true);
    try {
      const { data } = await api.post('/orders', { ...form, items: validItems });
      nav(`/orders/${data.id}`);
    } catch(e) {
      const err = e.response?.data;
      if (err?.existing_id) setError(`${err.error} — Open that order instead?`);
      else setError(err?.error || 'Error saving order');
    } finally { setSaving(false); }
  };

  const inp = { ...S.input, fontSize:13 };

  return (
    <div style={{ maxWidth:1200 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
        <h1 style={{ fontSize:22, fontWeight:600, margin:0 }}>New order</h1>
        <button onClick={() => nav('/orders')} style={S.btnSecondary}>← Back</button>
      </div>

      {error && <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'10px 14px', marginBottom:16, color:'#dc2626', fontSize:13 }}>{error}</div>}

      {/* ORDER HEADER */}
      <div style={{ background:'#fff', borderRadius:10, border:'1px solid #e5e7eb', padding:22, marginBottom:16 }}>
        <div style={{ fontWeight:600, fontSize:15, marginBottom:16 }}>Order details</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:14 }}>
          <div>
            <label style={S.label}>Customer *</label>
            <select value={form.customer_id} onChange={e => onCustomer(e.target.value)} style={inp}>
              <option value="">Select customer...</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name} ({c.country})</option>)}
            </select>
          </div>
          <div><label style={S.label}>PI Number *</label><input value={form.pi_number} onChange={e => f('pi_number')(e.target.value)} placeholder="e.g. PI-2519" style={inp} /></div>
          <div><label style={S.label}>Customer PO</label><input value={form.customer_po} onChange={e => f('customer_po')(e.target.value)} placeholder="Their reference number" style={inp} /></div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:14 }}>
          <div><label style={S.label}>Order date *</label><input type="date" value={form.order_date} onChange={e => f('order_date')(e.target.value)} style={inp} /></div>
          {/* Fix #5: default year input to current year via min attribute */}
          <div><label style={S.label}>Expected ship date</label><input type="date" value={form.expected_ship_date} min={todayStr} onChange={e => f('expected_ship_date')(e.target.value)} style={inp} /></div>
          <div><label style={S.label}>Currency</label>
            <select value={form.currency} onChange={e => f('currency')(e.target.value)} style={inp}>
              <option>USD</option><option>EUR</option><option>INR</option>
            </select>
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginBottom:14 }}>
          <div><label style={S.label}>Shipping terms</label>
            <select value={form.shipping_terms} onChange={e => f('shipping_terms')(e.target.value)} style={inp}>
              <option>FOB</option><option>CIF</option><option>CFR</option><option>EXW</option><option>DAP</option>
            </select>
          </div>
          <div><label style={S.label}>Destination port</label><input value={form.destination_port} onChange={e => f('destination_port')(e.target.value)} placeholder="e.g. Jebel Ali, Dubai" style={inp} /></div>
          <div><label style={S.label}>HS Code</label><input value={form.hs_code} onChange={e => f('hs_code')(e.target.value)} style={inp} /></div>
        </div>
        <div style={{ marginBottom:12 }}><label style={S.label}>Payment terms</label><input value={form.payment_terms} onChange={e => f('payment_terms')(e.target.value)} style={inp} placeholder="e.g. 30% advance, 70% against BL copy" /></div>
        <div><label style={S.label}>Special instructions</label><textarea value={form.special_instructions} onChange={e => f('special_instructions')(e.target.value)} style={{ ...inp, height:56, resize:'vertical' }} /></div>
      </div>

      {/* LINE ITEMS */}
      <div style={{ background:'#fff', borderRadius:10, border:'1px solid #e5e7eb', padding:22, marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
          <div>
            <div style={{ fontWeight:600, fontSize:15 }}>Line items</div>
            <div style={{ fontSize:12, color:'#9ca3af', marginTop:2 }}>
              Tip: Copy rows from Excel and paste into row 1 — all columns fill automatically. Or paste a single column (size, qty, price) into any cell in that column.
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {totalValue > 0 && <span style={{ fontSize:13, color:'#6b7280' }}>Total: <strong style={{ color:'#111827' }}>{form.currency} {totalValue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</strong></span>}
            <button onClick={downloadTemplate} style={{ ...S.btnSecondary, fontSize:12, padding:'5px 11px' }}>⬇ CSV Template</button>
            <button onClick={addSectionHeader} style={{ ...S.btnSecondary, fontSize:12, padding:'5px 11px' }}>+ Section</button>
            <button onClick={addRow} style={{ ...S.btnSecondary, fontSize:12, padding:'5px 11px' }}>+ Row</button>
          </div>
        </div>

        <div style={{ overflowX:'auto' }} ref={tableRef}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
            <thead>
              <tr style={{ background:'#f9fafb' }}>
                <th style={{ ...S.th, width:30 }}>#</th>
                <th style={{ ...S.th, width:130 }}>Section header</th>
                <th style={{ ...S.th, width:130 }}>Product code</th>
                <th style={{ ...S.th, width:180 }}>Size / description *</th>
                <th style={{ ...S.th, width:90 }}>Qty *</th>
                <th style={{ ...S.th, width:110 }}>Price/pc</th>
                <th style={{ ...S.th }}>Line notes</th>
                <th style={{ ...S.th, width:40 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item,i) => (
                <tr key={i} style={{ borderBottom:'1px solid #f9fafb' }}>
                  <td style={{ padding:'4px 6px', color:'#9ca3af', fontSize:11, textAlign:'center' }}>{i+1}</td>
                  <td style={{ padding:'4px 5px' }}>
                    <input value={item.section_header}
                      onChange={e => updateRow(i,'section_header',e.target.value)}
                      onPaste={e => handlePaste(e, i, 'section_header')}
                      placeholder="e.g. NC Clips" style={{ ...inp, width:'100%' }} />
                  </td>
                  <td style={{ padding:'4px 5px', position:'relative' }}>
                    <input value={item.product_code}
                      onChange={e => { updateRow(i,'product_code',e.target.value); setShowProductSug(i); }}
                      onBlur={() => setTimeout(() => setShowProductSug(null), 180)}
                      placeholder="e.g. RL" style={{ ...inp, width:'100%' }} />
                    {showProductSug === i && prodSuggestions(item.product_code).length > 0 && (
                      <div style={{ position:'absolute', top:'100%', left:0, background:'#fff', border:'1px solid #e5e7eb', borderRadius:6, zIndex:200, minWidth:220, boxShadow:'0 4px 16px rgba(0,0,0,.1)' }}>
                        {prodSuggestions(item.product_code).map(p => (
                          <div key={p.id} onMouseDown={() => {
                            updateRow(i,'product_code',p.code);
                            setShowProductSug(null);
                            // Fix #3: load price suggestions on product select
                            if (form.customer_id) loadPriceSuggestions(form.customer_id, p.code, i);
                          }}
                            style={{ padding:'7px 12px', fontSize:12, cursor:'pointer', borderBottom:'1px solid #f3f4f6' }}>
                            <strong style={{ color:'#3b82f6' }}>{p.code}</strong> — {p.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding:'4px 5px', position:'relative' }}>
                    <input value={item.size}
                      onChange={e => { updateRow(i,'size',e.target.value); setShowSizeSug(i); setShowPriceSug(null); }}
                      onPaste={e => handlePaste(e, i, 'size')}
                      onBlur={() => setTimeout(() => setShowSizeSug(null), 180)}
                      placeholder="e.g. 20-25mm or 1 NO" style={{ ...inp, width:'100%' }} />
                    {/* Fix #3: size suggestions from previous orders */}
                    {showSizeSug === i && sizeSuggestions(item.size).length > 0 && (
                      <div style={{ position:'absolute', top:'100%', left:0, background:'#fff', border:'1px solid #e5e7eb', borderRadius:6, zIndex:200, minWidth:180, boxShadow:'0 4px 16px rgba(0,0,0,.1)' }}>
                        {sizeSuggestions(item.size).map(s => (
                          <div key={s} onMouseDown={() => { updateRow(i,'size',s); setShowSizeSug(null); }}
                            style={{ padding:'7px 12px', fontSize:12, cursor:'pointer', borderBottom:'1px solid #f3f4f6' }}>{s}</div>
                        ))}
                      </div>
                    )}
                    {/* Fix #3: price+size suggestions from customer's previous orders */}
                    {showPriceSug === i && priceSuggestions.length > 0 && (
                      <div style={{ position:'absolute', top:'100%', left:0, background:'#fff', border:'1px solid #bfdbfe', borderRadius:6, zIndex:210, minWidth:260, boxShadow:'0 4px 16px rgba(0,0,0,.12)' }}>
                        <div style={{ padding:'6px 10px', fontSize:11, color:'#3b82f6', fontWeight:600, background:'#eff6ff', borderBottom:'1px solid #bfdbfe' }}>
                          Last prices for this customer ↓
                        </div>
                        {priceSuggestions.map((s,idx) => (
                          <div key={idx} onMouseDown={() => applyPriceSuggestion(i, s)}
                            style={{ padding:'7px 12px', fontSize:12, cursor:'pointer', borderBottom:'1px solid #f3f4f6', display:'flex', justifyContent:'space-between' }}>
                            <span>{s.size}</span>
                            <span style={{ color:'#3b82f6', fontWeight:500 }}>${s.price_per_unit}</span>
                          </div>
                        ))}
                        <div onMouseDown={() => setShowPriceSug(null)}
                          style={{ padding:'5px 10px', fontSize:11, color:'#9ca3af', cursor:'pointer', textAlign:'center' }}>
                          Dismiss
                        </div>
                      </div>
                    )}
                  </td>
                  <td style={{ padding:'4px 5px' }}>
                    <input type="number" value={item.qty_ordered}
                      onChange={e => updateRow(i,'qty_ordered',e.target.value)}
                      onPaste={e => handlePaste(e, i, 'qty_ordered')}
                      placeholder="0" style={{ ...inp, width:'100%' }} />
                  </td>
                  <td style={{ padding:'4px 5px' }}>
                    <input type="number" value={item.price_per_unit}
                      onChange={e => updateRow(i,'price_per_unit',e.target.value)}
                      onPaste={e => handlePaste(e, i, 'price_per_unit')}
                      onFocus={() => {
                        // Fix #3: show price suggestions when focusing price field if product+customer set
                        if (form.customer_id && item.product_code && priceSuggestions.length === 0) {
                          loadPriceSuggestions(form.customer_id, item.product_code, i);
                        } else if (priceSuggestions.length > 0) {
                          setShowPriceSug(i);
                        }
                      }}
                      placeholder="0.000" step="0.001" style={{ ...inp, width:'100%' }} />
                  </td>
                  <td style={{ padding:'4px 5px' }}>
                    <input value={item.line_notes} onChange={e => updateRow(i,'line_notes',e.target.value)} placeholder="optional" style={{ ...inp, width:'100%' }} />
                  </td>
                  <td style={{ padding:'4px 5px', textAlign:'center' }}>
                    <div style={{ display:'flex', gap:3, justifyContent:'center' }}>
                      <button title="Duplicate row" onClick={() => duplicateRow(i)} style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:14, lineHeight:1 }}>⧉</button>
                      {items.length > 1 && <button title="Remove row" onClick={() => removeRow(i)} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:18, lineHeight:1 }}>×</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalValue > 0 && (
          <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #e5e7eb', display:'flex', justifyContent:'flex-end', fontSize:14 }}>
            <div>
              <span style={{ color:'#6b7280' }}>Total order value: </span>
              <strong style={{ color:'#3b82f6', fontSize:16 }}>{form.currency} {totalValue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</strong>
              <span style={{ color:'#9ca3af', fontSize:12, marginLeft:8 }}>({items.filter(i=>i.qty_ordered).reduce((s,i)=>s+(parseInt(i.qty_ordered)||0),0).toLocaleString()} pcs)</span>
            </div>
          </div>
        )}
      </div>

      <div style={{ display:'flex', gap:10, alignItems:'center' }}>
        <button onClick={save} disabled={saving} style={{ ...S.btnPrimary, padding:'10px 28px', fontSize:14 }}>{saving ? 'Saving...' : 'Save order'}</button>
        <button onClick={() => nav('/orders')} style={{ ...S.btnSecondary, padding:'10px 20px' }}>Cancel</button>
        {saving && <span style={{ color:'#9ca3af', fontSize:13 }}>Creating order...</span>}
      </div>
    </div>
  );
}
